import React, { useEffect, useState } from 'react';
import { useIntl } from '../../../util/reactIntl';
import { apiBaseUrl } from '../../../util/api';
import css from './OemOffersPanel.module.css';

/**
 * Verified manufacturer offers for a vehicle's make in the region of its listing, with an inline way to
 * ask about one offer.
 *
 * It shows only what the verified snapshot holds. The server has already dropped expired offers and any
 * offer that did not survive checking against the manufacturer's page. "No offers" and "could not verify"
 * are different statements, and a missing value is never shown as $0. The disclaimer travels with the
 * response and is always shown.
 *
 * Asking about an offer sends the offer's ID and the person's details, and nothing about the offer's
 * values: the server reads those itself. The inquiry is saved before the reply, and the reply says whether
 * it has reached the team or is saved and waiting to be sent.
 */

const LOADING = 'loading';

/** The date as the page wrote it, read as a calendar day (the time zone is not part of the date). */
const dayOf = (intl, iso) =>
  iso
    ? intl.formatDate(new Date(`${iso}T12:00:00Z`), { timeZone: 'UTC', dateStyle: 'long' })
    : null;

const money = (intl, amount) =>
  intl.formatNumber(amount, { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

/** One offer as a line of plain words: the rate, the cash, the lease, and the term. */
const describeOffer = (intl, o) => {
  const parts = [];
  if (o.offerType === 'finance_rate' && o.rate !== null) {
    parts.push(
      o.termMonths
        ? intl.formatMessage(
            { id: 'OemOffersPanel.financing' },
            { rate: o.rate, term: o.termMonths }
          )
        : intl.formatMessage({ id: 'OemOffersPanel.financingNoTerm' }, { rate: o.rate })
    );
  }
  if (o.cashbackCad !== null) {
    parts.push(
      intl.formatMessage({ id: 'OemOffersPanel.cash' }, { amount: money(intl, o.cashbackCad) })
    );
  }
  if (o.offerType === 'lease' && o.leaseMonthlyCad !== null) {
    const amount = money(intl, o.leaseMonthlyCad);
    parts.push(
      o.termMonths
        ? intl.formatMessage({ id: 'OemOffersPanel.lease' }, { amount, term: o.termMonths })
        : intl.formatMessage({ id: 'OemOffersPanel.leaseNoTerm' }, { amount })
    );
  }
  return parts.join(intl.formatMessage({ id: 'OemOffersPanel.joiner' }));
};

/** A new ID for each form the person opens, so a repeated send is recognised but a new ask is not. */
const newRequestId = () => {
  if (
    typeof window !== 'undefined' &&
    window.crypto &&
    typeof window.crypto.randomUUID === 'function'
  ) {
    return window.crypto.randomUUID();
  }
  return `r-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

/** The inline form for one offer. Its wording and consent are the only things it asks for. */
const InquiryForm = ({ offer, onDone, onCancel }) => {
  const intl = useIntl();
  const [requestId] = useState(newRequestId);
  const [values, setValues] = useState({
    name: '',
    email: '',
    phone: '',
    contactPreference: 'either',
    consent: false,
    promoOptIn: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = field => e => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const submit = async e => {
    e.preventDefault();
    if (!values.consent) {
      setError('consent');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await window.fetch(`${apiBaseUrl()}/api/oem-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          offerId: offer.id,
          requestId,
          name: values.name,
          email: values.email,
          phone: values.phone,
          contactPreference: values.contactPreference,
          consent: values.consent,
          promoOptIn: values.promoOptIn,
          campaign: 'oem_listing_panel',
        }),
      });
      const body = await res.json();
      if (res.ok && body.success) {
        onDone(body);
      } else {
        setError(body.code || 'failed');
      }
    } catch {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  const errorText =
    {
      invalid: 'OemOffersPanel.errorInvalid',
      offer_no_longer_current: 'OemOffersPanel.errorEnded',
      offer_not_found: 'OemOffersPanel.errorEnded',
      consent: 'OemOffersPanel.errorConsent',
      durable_storage_required: 'OemOffersPanel.errorSave',
      store_unavailable: 'OemOffersPanel.errorSave',
      not_saved: 'OemOffersPanel.errorSave',
      network: 'OemOffersPanel.errorNetwork',
    }[error] || 'OemOffersPanel.errorSave';

  return (
    <form className={css.form} onSubmit={submit} noValidate>
      <label className={css.field}>
        <span>{intl.formatMessage({ id: 'OemOffersPanel.formName' })}</span>
        <input
          type="text"
          autoComplete="name"
          required
          value={values.name}
          onChange={update('name')}
        />
      </label>
      <label className={css.field}>
        <span>{intl.formatMessage({ id: 'OemOffersPanel.formEmail' })}</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={values.email}
          onChange={update('email')}
        />
      </label>
      <label className={css.field}>
        <span>{intl.formatMessage({ id: 'OemOffersPanel.formPhone' })}</span>
        <input type="tel" autoComplete="tel" value={values.phone} onChange={update('phone')} />
      </label>
      <label className={css.field}>
        <span>{intl.formatMessage({ id: 'OemOffersPanel.formContactPreference' })}</span>
        <select value={values.contactPreference} onChange={update('contactPreference')}>
          <option value="either">
            {intl.formatMessage({ id: 'OemOffersPanel.contactEither' })}
          </option>
          <option value="email">{intl.formatMessage({ id: 'OemOffersPanel.contactEmail' })}</option>
          <option value="phone">{intl.formatMessage({ id: 'OemOffersPanel.contactPhone' })}</option>
        </select>
      </label>
      <label className={css.check}>
        <input type="checkbox" checked={values.consent} onChange={update('consent')} />
        <span>{intl.formatMessage({ id: 'OemOffersPanel.consent' })}</span>
      </label>
      <label className={css.check}>
        <input type="checkbox" checked={values.promoOptIn} onChange={update('promoOptIn')} />
        <span>{intl.formatMessage({ id: 'OemOffersPanel.promoOptIn' })}</span>
      </label>
      {error ? (
        <p className={css.error} role="alert">
          {intl.formatMessage({ id: errorText })}
        </p>
      ) : null}
      <div className={css.formActions}>
        <button type="submit" className={css.action} disabled={submitting}>
          {intl.formatMessage({
            id: submitting ? 'OemOffersPanel.submitting' : 'OemOffersPanel.submit',
          })}
        </button>
        <button type="button" className={css.secondary} onClick={onCancel}>
          {intl.formatMessage({ id: 'OemOffersPanel.cancel' })}
        </button>
      </div>
    </form>
  );
};

/** What the person is told after saving: sent to the team only when the team has it. */
const Confirmation = ({ result }) => {
  const intl = useIntl();
  const id = result.trackingId;
  const sent = result.status === 'crm_delivered';
  return (
    <p className={css.confirmation} role="status">
      {intl.formatMessage(
        { id: sent ? 'OemOffersPanel.sentConfirmation' : 'OemOffersPanel.savedConfirmation' },
        { id }
      )}
    </p>
  );
};

const OemOffersPanel = props => {
  const { make, province } = props;
  const intl = useIntl();
  const [state, setState] = useState({ status: LOADING });
  const [askingIndex, setAskingIndex] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    if (!make || !province) return undefined;
    let active = true;
    const query = `make=${encodeURIComponent(make)}&province=${encodeURIComponent(province)}`;
    setState({ status: LOADING });
    // A missing fetch is a failed request, not an error that stops the listing page rendering.
    const request =
      typeof window.fetch === 'function'
        ? window.fetch(`${apiBaseUrl()}/api/oem-programs?${query}`)
        : Promise.reject(new Error('fetch is not available'));
    request
      .then(res => res.json())
      .then(body => {
        if (active) setState(body && body.success ? body : { status: 'unavailable' });
      })
      .catch(() => {
        if (active) setState({ status: 'unavailable' });
      });
    return () => {
      active = false;
    };
  }, [make, province]);

  if (!make || !province) return null;

  const regionName = state.regionName || province;
  const offers = state.offers || [];
  const checked = dayOf(intl, state.lastCheckedAt ? state.lastCheckedAt.slice(0, 10) : null);

  let body;
  if (state.status === LOADING) {
    body = <p className={css.status}>{intl.formatMessage({ id: 'OemOffersPanel.loading' })}</p>;
  } else if (state.status === 'unavailable') {
    body = <p className={css.status}>{intl.formatMessage({ id: 'OemOffersPanel.unavailable' })}</p>;
  } else {
    // A verified snapshot whose offers have all ended is "no current offers", not a failed check.
    const verifiedNone =
      state.status === 'verified_no_offers' || state.status === 'verified_offers';
    body = (
      <>
        {offers.length > 0 ? (
          <ul className={css.offers}>
            {offers.map((o, i) => (
              <li key={o.id || `${o.model}-${o.offerType}-${i}`} className={css.offer}>
                <span className={css.model}>
                  {o.modelYear} {o.model}
                  {o.trim ? ` ${o.trim}` : ''}
                </span>
                <span className={css.terms}>{describeOffer(intl, o)}</span>
                <span className={css.ends}>
                  {intl.formatMessage(
                    { id: 'OemOffersPanel.endsOn' },
                    { date: dayOf(intl, o.expiryDate) }
                  )}
                </span>
                {o.eligibility ? (
                  <span className={css.eligibility}>
                    {intl.formatMessage(
                      { id: 'OemOffersPanel.eligibility' },
                      { text: o.eligibility }
                    )}
                  </span>
                ) : null}
                {o.id && confirmed === null ? (
                  <button
                    type="button"
                    className={css.action}
                    aria-expanded={askingIndex === i}
                    onClick={() => setAskingIndex(askingIndex === i ? null : i)}
                  >
                    {intl.formatMessage({ id: 'OemOffersPanel.askAboutOffer' })}
                  </button>
                ) : null}
                {askingIndex === i && confirmed === null ? (
                  <InquiryForm
                    offer={o}
                    onDone={result => {
                      setConfirmed(result);
                      setAskingIndex(null);
                    }}
                    onCancel={() => setAskingIndex(null)}
                  />
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {confirmed ? <Confirmation result={confirmed} /> : null}
        {offers.length === 0 && verifiedNone ? (
          <p className={css.status}>
            {intl.formatMessage({ id: 'OemOffersPanel.noOffers' }, { date: checked || '' })}
          </p>
        ) : null}
        {offers.length === 0 && !verifiedNone ? (
          <p className={css.status}>{intl.formatMessage({ id: 'OemOffersPanel.unverified' })}</p>
        ) : null}
        {checked && state.status !== LOADING ? (
          <p className={css.checked}>
            {intl.formatMessage({ id: 'OemOffersPanel.checkedOn' }, { date: checked })}
          </p>
        ) : null}
        {state.sourceUrl ? (
          <a className={css.link} href={state.sourceUrl} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage({ id: 'OemOffersPanel.sourceLink' })}
          </a>
        ) : null}
      </>
    );
  }

  return (
    <section className={css.root} aria-labelledby="oem-offers-heading">
      <p className={css.eyebrow}>{intl.formatMessage({ id: 'OemOffersPanel.eyebrow' })}</p>
      <h2 id="oem-offers-heading" className={css.heading}>
        {intl.formatMessage({ id: 'OemOffersPanel.heading' }, { make, region: regionName })}
      </h2>
      <p className={css.advantage}>{intl.formatMessage({ id: 'OemOffersPanel.advantage' })}</p>
      <p className={css.benefit}>{intl.formatMessage({ id: 'OemOffersPanel.benefit' })}</p>
      {body}
      {state.disclaimer ? <p className={css.disclaimer}>{state.disclaimer}</p> : null}
    </section>
  );
};

export default OemOffersPanel;
